import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  Chip,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import { onAllOders, onDownloadLabel } from "../../redux/homeAction";
import "bootstrap/dist/css/bootstrap.min.css";
import CustomPagination from "../CustomPagination";
import { useNavigate } from "react-router-dom";

const OrderManage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loader, setLoader] = useState(false);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [activeStatus, setActiveStatus] = useState("all");

  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [bulkDownloading, setBulkDownloading] = useState(false);

  const fetchOrders = () => {
    dispatch(onAllOders(setLoader, setOrders, page + 1, limit, activeStatus));
  };

  useEffect(() => {
    fetchOrders();
  }, [page, limit, activeStatus]);

  const handleSelectOrder = (id) => {
    setSelectedOrderIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const activeIds = orders?.orders?.map((o) => o._id) || [];
      setSelectedOrderIds(activeIds);
    } else {
      setSelectedOrderIds([]);
    }
  };

  // 📄 Redux Dispatch Handler for Single Label
  const downloadSingleLabel = (orderId, orderDisplayId) => {
    dispatch(onDownloadLabel(orderId, orderDisplayId));
  };

  const getStatusChip = (status) => {
    const maps = {
      pending: { color: "warning", text: "Pending" },
      confirmed: { color: "info", text: "Confirmed" },
      shipped: { color: "primary", text: "Shipped" },
      delivered: { color: "success", text: "Delivered" },
      cancelled: { color: "error", text: "Cancelled" },
      returned: { color: "success", text: "Returned" },
    };
    const target = maps[status] || { color: "default", text: status };
    return (
      <Chip
        label={target?.text?.toUpperCase()}
        color={target?.color}
        size="small"
        style={{ fontWeight: 700, fontSize: "10px" }}
      />
    );
  };

  const statuses = [
    { label: "All", value: "all" },
    { label: "Pending", value: "pending" },
    { label: "Confirmed", value: "confirmed" },
    { label: "Shipped", value: "shipped" },
    { label: "Delivered", value: "delivered" },
    { label: "Cancelled", value: "cancelled" },
    { label: "Returned", value: "returned" },
  ];

  const totalPages = orders?.pagination?.totalPages || 1;

  return (
    <div
      style={{
        padding: "24px",
        backgroundColor: "#f3f4f6",
        minHeight: "100vh",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        <div>
          <Typography
            variant="h4"
            style={{ fontWeight: 800, color: "#111827" }}
          >
            Order Management
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Filter orders by status and manage shipments.
          </Typography>
        </div>

        {selectedOrderIds.length > 0 && (
          <Button
            variant="contained"
            color="secondary"
            onClick={async () => {
              setBulkDownloading(true);
              for (const id of selectedOrderIds) {
                const orderObj = orders?.orders?.find((o) => o._id === id);
                if (orderObj?.shiprocket?.shipmentId) {
                  // Bulk download ke liye bhi dispatch use kar sakte hain ya loop mein action call kar sakte hain
                  await dispatch(onDownloadLabel(id, orderObj.orderId));
                }
              }
              setBulkDownloading(false);
              setSelectedOrderIds([]);
            }}
            disabled={bulkDownloading}
          >
            {bulkDownloading ? (
              <CircularProgress
                size={20}
                style={{ color: "#fff", marginRight: 8 }}
              />
            ) : null}
            Download Labels ({selectedOrderIds.length})
          </Button>
        )}
      </div>

      {/* 🏷️ Status Filter Tabs Bar */}
      <div
        style={{
          display: "flex",
          gap: "8px",
          marginBottom: "20px",
          flexWrap: "wrap",
        }}
      >
        {statuses.map((item) => (
          <Button
            key={item.value}
            variant={activeStatus === item.value ? "contained" : "outlined"}
            onClick={() => {
              setActiveStatus(item.value);
              setPage(0);
            }}
            style={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "20px",
              padding: "6px 16px",
              backgroundColor:
                activeStatus === item.value ? "#111827" : "#ffffff",
              color: activeStatus === item.value ? "#ffffff" : "#374151",
              borderColor: "#d1d5db",
            }}
          >
            {item.label}
          </Button>
        ))}
      </div>

      {/* 📋 Orders Data Table */}
      <TableContainer
        component={Paper}
        style={{ borderRadius: "12px", border: "1px solid #e5e7eb" }}
      >
        <Table>
          <TableHead style={{ backgroundColor: "#1f2937" }}>
            <TableRow>
              <TableCell style={{ width: "50px" }}>
                <Checkbox
                  onChange={handleSelectAll}
                  checked={
                    orders?.orders?.length > 0 &&
                    selectedOrderIds.length === orders?.orders?.length
                  }
                  style={{ color: "#ffffff" }}
                />
              </TableCell>
              <TableCell style={{ color: "#f9fafb", fontWeight: 700 }}>
                ORDER ID
              </TableCell>
              <TableCell style={{ color: "#f9fafb", fontWeight: 700 }}>
                CUSTOMER
              </TableCell>
              <TableCell style={{ color: "#f9fafb", fontWeight: 700 }}>
                ITEMS SUMMARY
              </TableCell>
              <TableCell style={{ color: "#f9fafb", fontWeight: 700 }}>
                STATUS
              </TableCell>
              <TableCell style={{ color: "#f9fafb", fontWeight: 700 }}>
                GRAND TOTAL
              </TableCell>
              <TableCell
                style={{ color: "#f9fafb", fontWeight: 700 }}
                align="center"
              >
                ACTIONS
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody style={{ backgroundColor: "#ffffff" }}>
            {loader ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  align="center"
                  style={{ padding: "40px" }}
                >
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : orders?.orders?.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  align="center"
                  style={{ padding: "30px", color: "#6b7280" }}
                >
                  No orders found for this status.
                </TableCell>
              </TableRow>
            ) : (
              orders?.orders?.map((order) => (
                <TableRow key={order?._id} hover>
                  <TableCell>
                    <Checkbox
                      checked={selectedOrderIds.includes(order._id)}
                      onChange={() => handleSelectOrder(order._id)}
                    />
                  </TableCell>
                  <TableCell style={{ fontWeight: 700, color: "#111827" }}>
                    #{order?.orderId}
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#6b7280",
                        fontWeight: 400,
                      }}
                    >
                      {order.createdAt
                        ? new Date(order.createdAt).toLocaleDateString()
                        : ""}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                      {order?.user?.name || "Guest"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                      {order?.totalItems} Item(s)
                    </Typography>
                    <Typography variant="caption" style={{ color: "#6b7280" }}>
                      {order?.products?.[0]?.name}{" "}
                      {order?.totalItems > 1
                        ? `+ ${order.totalItems - 1} more`
                        : ""}
                    </Typography>
                  </TableCell>
                  <TableCell>{getStatusChip(order?.status)}</TableCell>
                  <TableCell style={{ fontWeight: 700, color: "#111827" }}>
                    ₹{order?.totals?.grandTotal}
                  </TableCell>
                  <TableCell align="center">
                    <div
                      style={{
                        display: "flex",
                        gap: "6px",
                        justifyContent: "center",
                      }}
                    >
                      <Button
                        variant="contained"
                        size="small"
                        style={{
                          backgroundColor: "#111827",
                          textTransform: "none",
                        }}
                        onClick={() =>
                          navigate(`/admin/order/${order._id}`, {
                            state: { order },
                          })
                        }
                      >
                        View
                      </Button>

                      {order?.shiprocket?.shipmentId && (
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          style={{ textTransform: "none" }}
                          onClick={() =>
                            downloadSingleLabel(order._id, order.orderId)
                          }
                        >
                          Label
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <div
          style={{
            marginTop: "24px",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <CustomPagination
            page={page}
            totalPages={totalPages}
            onPageChange={(p) => setPage(p)}
          />
        </div>
      )}
    </div>
  );
};

export default OrderManage;
